import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLinkWithHref } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';

import { QuillEditorComponent, QuillViewComponent } from 'ngx-quill';

import { WhatsNew } from '@Models';
import { AdminWhatsNewService } from '@Services';
import { decodeGzipB64 } from '@Utils';
import { LoadingSpinnerComponent } from '../../../loading-spinner/loading-spinner.component';
import { ConfirmationDialogueComponent } from '../../../confirmation-dialogue/confirmation-dialogue.component';

@Component({
  selector: 'app-version-editor',
  standalone: true,
  imports: [
    FormsModule,
    QuillEditorComponent,
    QuillViewComponent,
    ReactiveFormsModule,
    RouterLinkWithHref,
    NgClass,
    LoadingSpinnerComponent,
    ConfirmationDialogueComponent,
  ],
  templateUrl: './version-editor.component.html',
  styleUrl: './version-editor.component.css',
})
export class VersionEditorComponent implements OnInit {
  whatsNewContent: WhatsNew = {
    whatsNewGuid: '',
    createdDate: new Date(),
    updatedDate: new Date(),
    releasedDate: new Date(),
    version: '',
    status: 'draft',
    show: false,
    content: '',
  };

  isVersionFormatValid: null | boolean = null;
  formSubmitted = false;

  isEditing = false;
  showConfirmDialog = false;

  @ViewChild('pageTitle') pageTitle!: ElementRef<HTMLHeadingElement>;
  alertType: 'success' | 'danger' | 'info' = 'info';
  isAlertVisible = false;
  alertMessage = '';
  isSaving = false;
  isLoading = false;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _whatsNewService: AdminWhatsNewService,
  ) {}

  ngOnInit(): void {
    const id = this._route.snapshot.paramMap.get('id');
    if (id) {
      console.log('id', id);
      this.isEditing = true;
      this.loadVersion(id);
    }
  }

  onSubmit() {
    this.formSubmitted = true;

    this.validateVersion();

    this.focusTitle();

    if (!this.isVersionFormatValid) {
      return this.showAlert('Version number is invalid', 'danger');
    }

    this.isSaving = true;
    if (this.whatsNewContent.show === ('true' as unknown as boolean)) {
      this.whatsNewContent.show = true;
    }
    if (this.whatsNewContent.show === ('false' as unknown as boolean)) {
      this.whatsNewContent.show = false;
    }
    if (this.whatsNewContent.status) {
      console.log('status', this.whatsNewContent.status);
      switch (this.whatsNewContent.status) {
        case 'draft':
          this.whatsNewContent.show = false;
          break;

        case 'published':
          this.whatsNewContent.show = true;
          break;

        case 'archived':
          this.whatsNewContent.show = false;
          break;

        case 'review':
          this.whatsNewContent.show = false;
          break;

        default:
          this.whatsNewContent.show = false;
          break;
      }
    }

    if (this.isEditing) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { createdDate, releasedDate, ...requestBody } = this.whatsNewContent;
      return this._whatsNewService.updateWhatsNewSection(requestBody).subscribe({
        next: () => {
          this.isSaving = false;
          this.isEditing = true;
          this.isAlertVisible = true;
          this.focusTitle();
          this.showAlert('Version updated successfully', 'success');
        },
        error: (error) => {
          this.isSaving = false;
          console.log('error', error);
        },
      });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { whatsNewGuid, createdDate, releasedDate, updatedDate, ...requestBody } = this.whatsNewContent;
    return this._whatsNewService.createWhatsNewSection(requestBody).subscribe({
      next: (response) => {
        this.isSaving = false;
        this.whatsNewContent.whatsNewGuid = response.guid;
        this.isEditing = true;
        this.isAlertVisible = true;
        this.showAlert('Version added successfully');
      },
      error: (error) => {
        this.isSaving = false;
        this.showAlert('Error', 'danger');
        console.log('error', error);
      },
    });
  }

  onCancel() {
    this._router.navigate(['admin/whats-new']).then();
  }

  onDelete() {
    this.showConfirmDialog = true;
  }

  onConfirmDelete() {
    this.showConfirmDialog = false;
    this._whatsNewService.deleteWhatsNewSection(this.whatsNewContent.whatsNewGuid).subscribe({
      next: () => {
        console.log('Item deleted successfully');
        this._router.navigate(['admin/whats-new']).then(() => {
          console.log('Navigated back to WhatsNew list');
        });
      },
      error: (err) => {
        console.error('Failed to delete item', err);
      },
    });
  }

  onCancelDelete() {
    this.showConfirmDialog = false;
  }

  loadVersion(id: string): void {
    this.isLoading = true;
    this._whatsNewService.getWhatsNewSectionByGuid(id).subscribe((response) => {
      this.isLoading = false;
      if (response) {
        this.whatsNewContent = { ...response, content: decodeGzipB64(response.content) };
      } else {
        console.log('Version not found!');
      }
    });
  }

  validateVersion(): boolean {
    const versionRegex = /^\d+\.\d+\.\d+$/;
    return (this.isVersionFormatValid = versionRegex.test(this.whatsNewContent.version));
  }

  showAlert(message: string, type: 'success' | 'danger' | 'info' = 'info') {
    this.alertMessage = message.trim();
    this.alertType = type;
    this.isAlertVisible = true;

    // Hide the alert after 8 seconds
    setTimeout(() => {
      this.isAlertVisible = false;
    }, 8000);
  }

  dismissAlert(): void {
    this.isAlertVisible = false;
    this.alertMessage = '';
  }

  focusTitle() {
    if (this.pageTitle) {
      this.pageTitle.nativeElement.focus();
      requestAnimationFrame(() => {
        this.pageTitle.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }
}
